import { Box, Button, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectChangeEvent } from '@mui/material/Select';
import { AlertDialog } from '../../../components/AlertDialog';
import { ModalDialog } from '../../../components/ModalDialog';
import {
  MasterListJournal,
  MasterListJournalRequestBody,
  MaterialListStatus,
} from '../../../types/MasterList.types';
import { ErrorObject } from '../../../types/errorhandling.types';
import { StyledInputLabel } from '../../../components/inputs/InputLabel';
import { toFieldErrors, translateFieldError } from '../../../utils/apiErrors';

import { useMasterListJournalFormState } from '../hooks/useMasterListJournalFormState';
import { useMasterListJournalOptions } from '../hooks/useMasterListJournalOptions';
import {
  MasterListJournalForm,
  MasterListJournalFormData,
} from './MasterListJournalForm';
import { useGetMasterListHistory } from '../../../hooks/api/useMasterList';
import { MasterListJournalHistory } from './MasterListJournalHistory';

const formData: MasterListJournalFormData = {
  name: '',
  publisher: '',
  licenses: [],
  publishing_model: '',
  issn_e: '',
  issn_p: '',
  subjects: '',
  url: '',
  change_message: '',
};

interface MasterListJournalModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (
    journal: MasterListJournal,
    change_message: string
  ) => Promise<void>;
  onUpdate: (
    journalId: string,
    delta: MasterListJournalRequestBody['delta'],
    change_message: string
  ) => Promise<void>;
  onRevert: (
    journalId: string,
    status: MaterialListStatus | null | undefined
  ) => Promise<void>;
  onCease: (journalId: string, changeMessage: string) => Promise<void>;
  journal?: MasterListJournal;
  editMode?: boolean;
}

export function MasterListJournalModal({
  open,
  onClose,
  onCreate,
  onUpdate,
  onRevert,
  onCease,
  journal,
  editMode = false,
}: MasterListJournalModalProps) {
  const { t } = useTranslation();
  const [requestBody, setRequestBody] =
    useState<MasterListJournalFormData>(formData);
  const [isRevertAlertOpen, setIsRevertAlertOpen] = useState(false);
  const [isCeaseAlertOpen, setIsCeaseAlertOpen] = useState(false);
  const [ceaseChangeMessage, setCeaseChangeMessage] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [errors, setErrors] = useState<ErrorObject | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { licenseOptions, publisherOptions, publishingModelOptions } =
    useMasterListJournalOptions(open);
  const { masterListHistory } = useGetMasterListHistory(journal?.id ?? '');

  const { name, publisher, licenses, publishing_model, url, change_message } =
    requestBody;

  const initialState = useMemo(
    () => ({
      name: journal?.name ?? '',
      publisher: journal?.publisher ?? '',
      imprint: journal?.imprint ?? '',
      licenses: journal?.licenses ?? [],
      publishing_model: journal?.publishing_model ?? '',
      issn_e: journal?.issn_e ?? '',
      issn_p: journal?.issn_p ?? '',
      subjects: journal?.subjects ?? '',
      url: journal?.url ?? '',
      change_message: '',
    }),
    [journal]
  );

  useEffect(() => {
    if (!open) return;

    setRequestBody(initialState);
    clearErrors();
  }, [open, journal]);

  const requiredFieldsMissing = useMemo(
    () =>
      !name?.trim() ||
      !publisher?.trim() ||
      !publishing_model?.trim() ||
      !url?.trim() ||
      !change_message?.trim() ||
      licenses?.length === 0,
    [name, publisher, publishing_model, url, change_message, licenses]
  );
  const hasChanges = useMemo(
    () => JSON.stringify(requestBody) !== JSON.stringify(initialState),
    [requestBody, initialState]
  );
  const {
    isReadonly,
    showHeaderActions,
    showDeleteButton,
    showSaveButton,
    cancelLabel,
    canSave,
    showAsteriskSubtitle,
  } = useMasterListJournalFormState({
    editMode,
    journal,
    requiredFieldsMissing,
    hasChanges,
  });

  const isFormDataKey = (key: string): key is keyof MasterListJournalFormData =>
    key in initialState;

  function clearErrors() {
    setError(undefined);
    setErrors(undefined);
  }

  /** Keeps the dialog open and reports what the backend rejected. */
  const handleRequestError = (err: unknown) => {
    const fieldErrors = toFieldErrors(err);
    setErrors(fieldErrors);

    // Errors on fields the form has no input for can't be shown next to one.
    const unplaced = Object.entries(fieldErrors ?? {})
      .filter(([field]) => !isFormDataKey(field))
      .flatMap(([, messages]) =>
        messages.map((message) => translateFieldError(t, message))
      );

    if (!fieldErrors) {
      setError(t('masterList.saveError') as string);
    } else if (unplaced.length) {
      setError(unplaced.join(' '));
    }
  };

  const handleChange = (event: {
    target: { name?: string; value: unknown };
  }) => {
    if (isReadonly) return;
    const { name, value } = event.target;
    if (!name || !isFormDataKey(name)) return;

    clearErrors();
    setRequestBody((prev) => ({ ...prev, [name]: value }));
  };

  const handleLicenseChange = (event: SelectChangeEvent<string[]>) => {
    if (isReadonly) return;
    clearErrors();
    setRequestBody((prev) => ({
      ...prev,
      licenses: event.target.value as string[],
    }));
  };

  const handleClose = () => {
    setRequestBody(formData);
    clearErrors();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (requiredFieldsMissing || isSubmitting) return;

    const { change_message: msg, ...delta } = requestBody;
    if (editMode && !journal) return;

    clearErrors();
    setIsSubmitting(true);
    try {
      if (editMode && journal) {
        const changedDelta = Object.fromEntries(
          Object.entries(delta).filter(([key, value]) => {
            const initialValue = initialState[key as keyof typeof initialState];
            if (Array.isArray(value) && Array.isArray(initialValue)) {
              return JSON.stringify(value) !== JSON.stringify(initialValue);
            }
            return value !== initialValue;
          })
        );

        await onUpdate(journal.id, changedDelta, msg);
      } else {
        await onCreate(delta as MasterListJournal, msg);
      }
      handleClose();
    } catch (err: unknown) {
      handleRequestError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRevertChanges = () => {
    if (!journal) return;
    setIsRevertAlertOpen(true);
  };

  const handleDelete = () => {
    if (!journal) return;
    setIsCeaseAlertOpen(true);
  };

  const handleConfirmRevert = async () => {
    if (!journal) return;
    setIsRevertAlertOpen(false);
    clearErrors();
    try {
      await onRevert(journal.id, journal.status);
      handleClose();
    } catch (err: unknown) {
      handleRequestError(err);
    }
  };

  const handleCloseCeaseAlert = () => {
    setIsCeaseAlertOpen(false);
    setCeaseChangeMessage('');
  };

  const handleConfirmCease = async () => {
    if (!journal || !ceaseChangeMessage.trim()) return;
    const changeMessage = ceaseChangeMessage;
    handleCloseCeaseAlert();
    clearErrors();
    try {
      await onCease(journal.id, changeMessage);
      handleClose();
    } catch (err: unknown) {
      handleRequestError(err);
    }
  };

  return (
    <>
      <ModalDialog
        open={open}
        onClose={handleClose}
        title={
          editMode ? t('masterList.editJournal') : t('masterList.createJournal')
        }
        subtitle={
          showAsteriskSubtitle
            ? (t('agreements.asteriskRequired') as string)
            : undefined
        }
        error={error}
        headerAction={
          showHeaderActions ? (
            <Box sx={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" onClick={handleRevertChanges}>
                {t('masterList.revertChanges')}
              </Button>
              {showDeleteButton && (
                <Button variant="warning" onClick={handleDelete}>
                  {t('masterList.ceaseJournal')}
                </Button>
              )}
            </Box>
          ) : undefined
        }
        actions={
          <>
            <Button variant="default" onClick={handleClose}>
              {cancelLabel}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="master-list-journal-form"
              disabled={!canSave || isSubmitting}
              sx={{ display: showSaveButton ? 'inline-flex' : 'none' }}
            >
              {editMode ? 'Save' : 'Create'}
            </Button>
          </>
        }
      >
        <form id="master-list-journal-form" onSubmit={handleSubmit}>
          <MasterListJournalForm
            value={requestBody}
            disabled={isReadonly}
            errors={errors}
            licenseOptions={licenseOptions}
            publisherOptions={publisherOptions}
            publishingModelOptions={publishingModelOptions}
            onFieldChange={handleChange}
            onLicenseChange={handleLicenseChange}
          />
        </form>
        {masterListHistory.length > 0 && (
          <MasterListJournalHistory history={masterListHistory} />
        )}
      </ModalDialog>

      <AlertDialog
        variant="default"
        open={isRevertAlertOpen}
        onClose={() => setIsRevertAlertOpen(false)}
        title={t('masterList.revertChangesTitle')}
        description={t('masterList.revertChangesDescription')}
        actions={{
          cancel: {
            label: t('masterList.cancel'),
            onClick: () => setIsRevertAlertOpen(false),
          },
          confirm: {
            label: t('masterList.revert'),
            onClick: handleConfirmRevert,
          },
        }}
      />

      <AlertDialog
        variant="alert"
        open={isCeaseAlertOpen}
        onClose={handleCloseCeaseAlert}
        title={t('masterList.ceaseJournalTitle')}
        description={t('masterList.ceaseJournalDescription')}
        actions={{
          cancel: {
            label: t('masterList.cancel'),
            onClick: handleCloseCeaseAlert,
          },
          confirm: {
            label: t('masterList.cease'),
            onClick: handleConfirmCease,
            disabled: !ceaseChangeMessage.trim(),
          },
        }}
      >
        <Box sx={{ mt: 12 }}>
          <StyledInputLabel htmlFor="cease-change-message">
            Change message <span aria-hidden="true">*</span>
          </StyledInputLabel>
          <TextField
            fullWidth
            id="cease-change-message"
            value={ceaseChangeMessage}
            onChange={(e) => setCeaseChangeMessage(e.target.value)}
          />
        </Box>
      </AlertDialog>
    </>
  );
}
